import { memo, useState, useCallback } from 'react';
import {
  Button,
  Input,
  Label,
  Separator,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Textarea,
} from '@flote/ui';
import { Loader2, Car, Tag, Palette } from 'lucide-react';
import type { VehicleType, VehicleStatus } from '@flote/types/delivery';
import type { Team } from '@flote/contracts/http';

export interface VehicleFormData {
  type: VehicleType;
  description?: string;
  licensePlate?: string;
  color?: string;
  teamId?: string;
  capacity?: number;
  notes?: string;
  status?: VehicleStatus;
}

const VEHICLE_TYPES: { value: VehicleType; label: string }[] = [
  { value: 'CAR', label: 'Car' },
  { value: 'TRUCK', label: 'Truck' },
  { value: 'MOTORCYCLE', label: 'Motorcycle' },
  { value: 'BICYCLE', label: 'Bicycle' },
];

const VEHICLE_STATUSES: { value: VehicleStatus; label: string }[] = [
  { value: 'available', label: 'Available' },
  { value: 'assigned', label: 'Assigned' },
  { value: 'maintenance', label: 'Maintenance' },
  { value: 'retired', label: 'Retired' },
];

interface VehicleFormProps {
  onSubmit: (data: VehicleFormData) => Promise<void>;
  isProcessing?: boolean;
  teams: Team[];
  initialData?: Partial<VehicleFormData>;
  mode?: 'create' | 'edit';
}

export const VehicleForm = memo<VehicleFormProps>(({
  onSubmit,
  isProcessing = false,
  teams,
  initialData,
  mode = 'create',
}) => {
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Form fields
  const [type, setType] = useState<VehicleType>(initialData?.type || 'CAR');
  const [description, setDescription] = useState(initialData?.description || '');
  const [licensePlate, setLicensePlate] = useState(initialData?.licensePlate || '');
  const [color, setColor] = useState(initialData?.color || '');
  const [teamId, setTeamId] = useState(initialData?.teamId || 'none');
  const [capacity, setCapacity] = useState(initialData?.capacity?.toString() || '');
  const [notes, setNotes] = useState(initialData?.notes || '');
  const [status, setStatus] = useState<VehicleStatus>(initialData?.status || 'available');

  // Validation errors
  const [errors, setErrors] = useState<Record<string, string>>({});

  const validate = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!type) {
      newErrors.type = 'Vehicle type is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const buildVehicleRequest = (): VehicleFormData => {
    const request: VehicleFormData = {
      type,
    };

    if (description.trim()) {
      request.description = description.trim();
    }

    if (licensePlate.trim()) {
      request.licensePlate = licensePlate.trim();
    }

    if (color.trim()) {
      request.color = color.trim();
    }

    if (teamId && teamId !== 'none') {
      request.teamId = teamId;
    }

    if (capacity) {
      const capacityNum = parseInt(capacity, 10);
      if (!isNaN(capacityNum) && capacityNum > 0) {
        request.capacity = capacityNum;
      }
    }

    if (notes.trim()) {
      request.notes = notes.trim();
    }

    if (mode === 'edit') {
      request.status = status;
    }

    return request;
  };

  const handleSubmit = useCallback(async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validate()) {
      return;
    }

    setIsSubmitting(true);

    try {
      const vehicleRequest = buildVehicleRequest();
      await onSubmit(vehicleRequest);
    } finally {
      setIsSubmitting(false);
    }
  }, [type, description, licensePlate, color, teamId, capacity, notes, status, onSubmit, mode]);

  const processing = isSubmitting || isProcessing;

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      {/* Vehicle Type Section */}
      <div className="space-y-4">
        <div className="flex items-center gap-2 text-sm font-medium">
          <Car className="h-4 w-4 text-muted-foreground" />
          Vehicle Information
        </div>

        <div className="space-y-2">
          <Label htmlFor="type">Vehicle Type *</Label>
          <Select value={type} onValueChange={(v) => setType(v as VehicleType)}>
            <SelectTrigger className={errors.type ? 'border-destructive' : ''}>
              <SelectValue placeholder="Select vehicle type" />
            </SelectTrigger>
            <SelectContent>
              {VEHICLE_TYPES.map((t) => (
                <SelectItem key={t.value} value={t.value}>
                  {t.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          {errors.type && (
            <p className="text-xs text-destructive">{errors.type}</p>
          )}
        </div>

        <div className="space-y-2">
          <Label htmlFor="description">Description</Label>
          <Input
            id="description"
            placeholder="e.g., 2022 Toyota Camry"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            disabled={processing}
          />
          <p className="text-xs text-muted-foreground">
            Make, model, year or other identifying info
          </p>
        </div>
      </div>

      <Separator />

      {/* Identification Section */}
      <div className="space-y-4">
        <div className="flex items-center gap-2 text-sm font-medium">
          <Tag className="h-4 w-4 text-muted-foreground" />
          Identification
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label htmlFor="licensePlate">License Plate</Label>
            <Input
              id="licensePlate"
              placeholder="e.g., ABC-1234"
              value={licensePlate}
              onChange={(e) => setLicensePlate(e.target.value)}
              disabled={processing}
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="color">Color</Label>
            <Input
              id="color"
              placeholder="e.g., White"
              value={color}
              onChange={(e) => setColor(e.target.value)}
              disabled={processing}
            />
          </div>
        </div>
      </div>

      <Separator />

      {/* Assignment Section */}
      <div className="space-y-4">
        <div className="flex items-center gap-2 text-sm font-medium">
          <Palette className="h-4 w-4 text-muted-foreground" />
          Assignment & Capacity
        </div>

        <div className="space-y-2">
          <Label htmlFor="teamId">Assigned Team</Label>
          <Select value={teamId} onValueChange={setTeamId}>
            <SelectTrigger>
              <SelectValue placeholder="Select a team (optional)" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="none">No team assigned</SelectItem>
              {teams.map((team) => (
                <SelectItem key={team.id} value={team.id}>
                  {team.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <p className="text-xs text-muted-foreground">
            Optionally assign this vehicle to a team
          </p>
        </div>

        <div className="space-y-2">
          <Label htmlFor="capacity">Delivery Capacity</Label>
          <Input
            id="capacity"
            type="number"
            min="1"
            placeholder="e.g., 20"
            value={capacity}
            onChange={(e) => setCapacity(e.target.value)}
            disabled={processing}
          />
          <p className="text-xs text-muted-foreground">
            Maximum number of deliveries this vehicle can handle
          </p>
        </div>

        {mode === 'edit' && (
          <div className="space-y-2">
            <Label htmlFor="status">Status</Label>
            <Select value={status} onValueChange={(v) => setStatus(v as VehicleStatus)}>
              <SelectTrigger>
                <SelectValue placeholder="Select status" />
              </SelectTrigger>
              <SelectContent>
                {VEHICLE_STATUSES.map((s) => (
                  <SelectItem key={s.value} value={s.value}>
                    {s.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        )}
      </div>

      <Separator />

      {/* Notes Section */}
      <div className="space-y-4">
        <div className="space-y-2">
          <Label htmlFor="notes">Notes</Label>
          <Textarea
            id="notes"
            placeholder="Any additional notes about this vehicle..."
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            disabled={processing}
            rows={3}
          />
        </div>
      </div>

      {/* Submit Button */}
      <div className="flex pt-2">
        <Button type="submit" disabled={processing} className="w-full">
          {processing ? (
            <>
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              {mode === 'create' ? 'Creating Vehicle...' : 'Updating Vehicle...'}
            </>
          ) : (
            mode === 'create' ? 'Create Vehicle' : 'Update Vehicle'
          )}
        </Button>
      </div>
    </form>
  );
});

VehicleForm.displayName = 'VehicleForm';
